import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, MessageCircle, Search, RefreshCw, Inbox, Building2, Zap, Paperclip, Image as ImageIcon, Code2, Plus, Sparkles, Lock, Unlock, Menu, Smartphone, Monitor, ArrowLeft, LogOut, MessageSquare, GraduationCap } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
}

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/conversations").then((r) => setConversations(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get("/conversations").then((r) => setConversations(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/conversations/${selected.wa_id}/messages`).then((r) => setMessages(r.data));
    api.post(`/conversations/${selected.wa_id}/read`).then(() => {
      setConversations((prev) => prev.map((c) => c.wa_id === selected.wa_id ? { ...c, unread_count: 0 } : c));
    }).catch(() => {});
    const t = setInterval(() => {
      api.get(`/conversations/${selected.wa_id}/messages`).then((r) => setMessages(r.data));
    }, 8000);
    return () => clearInterval(t);
  }, [selected]);

  const filtered = useMemo(() => {
    if (!q) return conversations;
    const s = q.toLowerCase();
    return conversations.filter(
      (c) => c.wa_id?.toLowerCase().includes(s) || c.profile_name?.toLowerCase().includes(s)
    );
  }, [conversations, q]);

  const openConversation = (wa_id, channel = "meta") => {
    load();
    setSelected({ wa_id, channel, profile_name: null, session_open: true });
  };

  const panels = (
    <>
      {(!mobileView || !selected) && (
        <aside className={`flex min-h-0 flex-col border-r border-border ${mobileView ? "h-full w-full border-r-0" : "h-full"}`}>
          <div className="border-b border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">Inbox</div>
                <h2 className="font-display text-xl font-bold tracking-tight">Conversations</h2>
              </div>
              <div className="flex items-center gap-1">
                <SendTemplateDialog
                  onSent={openConversation}
                  trigger={
                    <Button size="icon" variant="ghost" data-testid="new-conversation-sidebar-btn" aria-label="New conversation">
                      <Plus className="h-4 w-4" />
                    </Button>
                  }
                />
                <Button size="icon" variant="ghost" onClick={load} data-testid="refresh-conversations-btn" aria-label="Refresh">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 border border-input px-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search number or name"
                className="rounded-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="conversations-search-input"
              />
            </div>
          </div>
          <ScrollArea className="chat-scrollbar min-h-0 flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]>div]:w-full">
            {loading ? (
              <div className="p-6 text-center text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
                <Inbox className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Empty inbox</div>
                <p className="max-w-xs text-xs text-muted-foreground">
                  Incoming WhatsApp messages will land here once your webhook receives events.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border" data-testid="conversations-list">
                {filtered.map((c) => {
                  const isOpen = c.session_open;
                  const unread = c.unread_count || 0;
                  const hasUnread = unread > 0 && selected?.wa_id !== c.wa_id;
                  return (
                    <li key={c.wa_id} className="min-w-0">
                      <button
                        onClick={() => setSelected(c)}
                        data-testid={`conversation-${c.wa_id}`}
                        className={`relative flex w-full min-w-0 flex-col gap-1 border-l-2 px-4 py-3 text-left transition-colors duration-150 hover:bg-secondary ${
                          selected?.wa_id === c.wa_id ? "bg-secondary" : ""
                        } ${
                          isOpen
                            ? (c.channel === "interakt" ? "border-l-fuchsia-500" : "border-l-emerald-500")
                            : "border-l-transparent opacity-60"
                        }`}
                      >
                        <div className="flex min-w-0 items-center justify-between gap-2">
                          <div className="flex min-w-0 items-center gap-2">
                            <div className={`min-w-0 truncate font-display text-sm ${hasUnread ? "font-extrabold text-foreground" : "font-bold"}`}>
                              {c.profile_name || `+${c.wa_id}`}
                            </div>
                            <ChannelBadge channel={c.channel} />
                            {c.locked && (
                              <Lock
                                className="h-3 w-3 shrink-0 text-red-500"
                                aria-label="Locked — admin only"
                                data-testid={`conv-locked-icon-${c.wa_id}`}
                              />
                            )}
                            {isOpen && (
                              <span
                                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                                title="24h session open"
                                data-testid={`session-open-${c.wa_id}`}
                              />
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {hasUnread && (
                              <span
                                className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 font-mono text-[9px] font-bold text-white"
                                title={`${unread} unread message${unread === 1 ? "" : "s"}`}
                                data-testid={`unread-badge-${c.wa_id}`}
                              >
                                {unread > 99 ? "99+" : unread}
                              </span>
                            )}
                            <div className="font-mono text-[10px] text-muted-foreground">{formatTime(c.last_ts)}</div>
                          </div>
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground">+{c.wa_id}</div>
                        <div className={`w-full min-w-0 truncate text-xs ${hasUnread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                          {c.last_direction === "out" ? "→ " : "← "}
                          {c.last_message}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
        </aside>
      )}

      {(!mobileView || selected) && (
        <section className={`flex min-h-0 flex-col bg-background ${mobileView ? "h-full w-full" : "h-full"}`}>
          {mobileView && selected && (
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-border bg-secondary px-3 py-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelected(null)}
                data-testid="mobile-back-btn"
                aria-label="Back to conversations"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                Back to inbox
              </span>
            </div>
          )}
          {selected ? (
            <ChatThread
              key={selected.wa_id}
              conversation={selected}
              messages={messages}
              onSent={() => api.get(`/conversations/${selected.wa_id}/messages`).then((r) => setMessages(r.data))}
              onLockChange={(waId, isLocked) => {
                setConversations((prev) => prev.map((c) => c.wa_id === waId ? { ...c, locked: isLocked } : c));
                setSelected((s) => (s && s.wa_id === waId ? { ...s, locked: isLocked } : s));
              }}
            />
          ) : (
            <EmptyChat />
          )}
        </section>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-background" data-testid="conversations-shell">
      <ConversationsTopNav mobileView={mobileView} onToggleMobileView={() => setMobileView((v) => !v)} />
      <div
        className={`min-h-0 flex-1 ${
          mobileView
            ? "flex justify-center overflow-hidden bg-gradient-to-b from-neutral-200 to-neutral-300 p-4 dark:from-neutral-900 dark:to-neutral-950"
            : "grid grid-cols-[340px_1fr] overflow-hidden"
        }`}
        data-testid={mobileView ? "conversations-mobile-backdrop" : "conversations-desktop-frame"}
      >
        {mobileView ? (
          <div
            className="flex h-full w-full max-w-[420px] flex-col overflow-hidden rounded-[28px] border-[10px] border-neutral-800 bg-background shadow-2xl dark:border-neutral-700"
            data-testid="conversations-mobile-frame"
          >
            {panels}
          </div>
        ) : (
          panels
        )}
      </div>
    </div>
  );
}

function ConversationsTopNav({ mobileView, onToggleMobileView }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const items = [
    { to: "/", label: "Dashboard", adminOnly: true, end: true },
    { to: "/conversations", label: "Conversations" },
    { to: "/templates", label: "Templates", adminOnly: true },
    { to: "/staff", label: "Staff", adminOnly: true },
    { to: "/keys", label: "API Keys", adminOnly: true },
    { to: "/settings", label: "Settings", adminOnly: true },
  ].filter((i) => !i.adminOnly || user?.role === "admin");

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header
      className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-border bg-background px-4 py-2.5"
      data-testid="conversations-top-nav"
    >
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleMobileView}
          className="h-8 rounded-sm text-xs"
          data-testid="toggle-mobile-view-btn"
          title={mobileView ? "Switch back to desktop layout" : "Preview mobile layout"}
        >
          {mobileView ? <Monitor className="mr-1.5 h-3.5 w-3.5" /> : <Smartphone className="mr-1.5 h-3.5 w-3.5" />}
          {mobileView ? "Desktop view" : "Mobile view"}
        </Button>
        {mobileView && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-hamburger-btn"
            aria-label="Open menu"
            className="h-8 w-8"
          >
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <div className="ml-1 hidden items-center gap-2 md:flex">
          <div className="flex h-7 w-7 items-center justify-center bg-primary text-primary-foreground">
            <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" />
          </div>
          <div className="font-display text-sm font-bold">WABA CRM</div>
        </div>
      </div>

      {!mobileView && (
        <nav className="hidden items-center gap-1 md:flex" data-testid="top-nav-desktop">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`top-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                  isActive
                    ? "border-b-2 border-primary text-foreground"
                    : "border-b-2 border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}

      <div className="flex items-center gap-2">
        <div className="hidden text-right md:block">
          <div className="font-mono text-[10px] text-muted-foreground">+91 {user?.mobile}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest">{user?.role}</div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onLogout}
          className="h-8 rounded-sm gap-1.5 text-xs"
          data-testid="top-nav-logout-btn"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72" data-testid="mobile-nav-drawer">
          <SheetHeader>
            <SheetTitle className="font-display">WABA CRM</SheetTitle>
          </SheetHeader>
          <nav className="mt-6 flex flex-col gap-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setDrawerOpen(false)}
                data-testid={`drawer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `border-l-2 px-3 py-2.5 text-sm ${
                    isActive
                      ? "border-primary bg-secondary text-foreground"
                      : "border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 border-t border-border pt-4">
            <div className="mb-2 font-mono text-[10px] text-muted-foreground">+91 {user?.mobile}</div>
            <Button
              variant="outline"
              onClick={() => { setDrawerOpen(false); onLogout(); }}
              className="w-full justify-start gap-2 rounded-sm"
              data-testid="drawer-logout-btn"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

function EmptyChat() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center border border-border">
        <MessageCircle className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      </div>
      <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Nothing selected</div>
      <h3 className="font-display text-2xl font-bold tracking-tight">Pick a conversation to start</h3>
      <p className="max-w-md text-sm text-muted-foreground">
        Select a chat from the left to view the full thread. To start a brand new conversation, send an approved
        template using the button below.
      </p>
      <SendTemplateDialog
        onSent={(waId, ch) => {
          // signal parent (Conversations) — not accessible here; toast is enough
        }}
        trigger={<Button className="rounded-sm" data-testid="new-conversation-btn"><Send className="mr-2 h-4 w-4" />New conversation</Button>}
      />
    </div>
  );
}

function ChannelBadge({ channel }) {
  const ch = (channel || "meta").toLowerCase();
  const isInterakt = ch === "interakt";
  const label = isInterakt ? "INTERAKT" : "META";
  const Icon = isInterakt ? Zap : Building2;
  const cls = isInterakt
    ? "border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300"
    : "border-primary/40 bg-primary/10 text-primary";
  return (
    <span
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[9px] font-bold tracking-widest ${cls}`}
      data-testid={`channel-badge-${ch}`}
    >
      <Icon className="h-2.5 w-2.5" aria-hidden="true" />
      {label}
    </span>
  );
}

function SessionDivider({ message, wa_id }) {
  const ch = (message.channel || "meta").toLowerCase();
  const dot = ch === "interakt" ? "bg-fuchsia-500" : "bg-emerald-500";
  const date = message.timestamp ? new Date(message.timestamp) : null;
  const label = date
    ? date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "New session";
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const closed = date ? (Date.now() - date.getTime()) > 24 * 60 * 60 * 1000 : true;
  const canSummarise = closed && wa_id && message.timestamp;

  const summarise = async () => {
    if (!canSummarise || loading) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/conversations/${wa_id}/session-summary`, { params: { anchor_ts: message.timestamp } });
      const s = r.data?.summary;
      if (s) setSummary(s);
      else setError(`Only ${r.data?.message_count || 0} message(s) in this session — nothing to summarise.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Summary failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="my-4" data-testid="session-divider">
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <div className="flex items-center gap-1.5 border border-border bg-background px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />
          Session · {label} · <ChannelBadge channel={ch} />
        </div>
        {canSummarise && !summary && !loading && (
          <button
            type="button"
            onClick={summarise}
            className="flex items-center gap-1 border border-border bg-background px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            data-testid={`session-summarise-btn-${message.timestamp}`}
            title="AI summary of this session (Claude)"
          >
            <Sparkles className="h-3 w-3" />
            Summarise
          </button>
        )}
        <div className="h-px flex-1 bg-border" />
      </div>
      {(loading || summary || error) && (
        <div
          className={`mx-auto mt-2 max-w-xl border px-3 py-1.5 text-center text-[11px] italic ${
            error ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "border-border bg-secondary text-muted-foreground"
          }`}
          data-testid="session-summary-chip"
        >
          {loading ? "Summarising session…" : (summary || error)}
        </div>
      )}
    </div>
  );
}

function ChatThread({ conversation, messages, onSent, onLockChange }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [togglingLock, setTogglingLock] = useState(false);
  const scrollRef = useRef(null);
  const lastCountRef = useRef(0);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const locked = !!conversation.locked;

  // Media URLs on the backend are auth-gated. <img>/<video>/<audio> tags cannot
  // attach the Authorization header, so we append ?token= for same-origin loads.
  const mediaToken = typeof window !== "undefined" ? localStorage.getItem("waba_token") : "";
  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const mediaUrlFor = (m) => {
    if (!m?.media?.url) return null;
    const base = m.media.url.startsWith("http") ? m.media.url : `${backendBase}${m.media.url}`;
    const sep = base.includes("?") ? "&" : "?";
    return mediaToken ? `${base}${sep}token=${encodeURIComponent(mediaToken)}` : base;
  };

  useEffect(() => {
    const vp = scrollRef.current;
    if (!vp) return;
    const isSwitch = messages.length < lastCountRef.current || lastCountRef.current === 0;
    const lastMsg = messages[messages.length - 1];
    const isOwnSend = lastMsg?.direction === "out";
    const nearBottom = vp.scrollHeight - vp.scrollTop - vp.clientHeight < 200;
    // Always scroll on switch, own send, or when user was already near bottom.
    if (isSwitch || isOwnSend || nearBottom) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = scrollRef.current;
          if (!el) return;
          el.scrollTop = el.scrollHeight;
        });
      });
    }
    lastCountRef.current = messages.length;
  }, [messages.length, conversation.wa_id]);

  const saveAsBotAnswer = async (m) => {
    try {
      await api.post("/bot/knowledge/from-message", { wa_id: conversation.wa_id, message_id: m.id });
      toast.success("Saved to bot knowledge base");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const lastInbound = [...messages].reverse().find((m) => m.direction === "in");
  const isInterakt = (conversation.channel || "meta") === "interakt";
  const withinWindow = isInterakt
    ? true
    : (lastInbound?.timestamp
        ? Date.now() - new Date(lastInbound.timestamp).getTime() < 24 * 60 * 60 * 1000
        : false);
  const hoursLeft = !isInterakt && lastInbound?.timestamp
    ? Math.max(0, Math.round(24 - (Date.now() - new Date(lastInbound.timestamp).getTime()) / 3600000))
    : 0;

  const sendText = async () => {
    const value = text.trim();
    if (!value) return;
    setSending(true);
    try {
      await api.post("/messages/send-text", { to: conversation.wa_id, body: value, channel: conversation.channel });
      setText("");
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText();
    }
  };

  const viewRaw = (m) => {
    const raw = JSON.stringify(m.raw || m, null, 2);
    navigator.clipboard?.writeText(raw).then(() => toast.info("Raw payload copied"));
    window.alert(raw.slice(0, 4000));
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="chat-thread-root">
      <header className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="truncate font-display text-lg font-bold" data-testid="chat-header-name">
              {conversation.profile_name || `+${conversation.wa_id}`}
            </div>
            <ChannelBadge channel={conversation.channel} />
          </div>
          <div className="font-mono text-xs text-muted-foreground">+{conversation.wa_id} · {messages.length} messages</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {lastInbound && !isInterakt && (
            <div
              className={`hidden items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest md:inline-flex ${
                withinWindow ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
              }`}
              data-testid="window-badge"
            >
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${withinWindow ? "bg-emerald-500" : "bg-amber-500"}`} />
              {withinWindow ? `Session open · ${hoursLeft}h left` : "Session closed"}
            </div>
          )}
          {isInterakt && (
            <div className="hidden items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-fuchsia-600 dark:text-fuchsia-400 md:inline-flex" data-testid="window-badge">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-fuchsia-500" />
              Coexistence · always open
            </div>
          )}
          <SendTemplateDialog
            defaultTo={conversation.wa_id.replace(/^91/, "")}
            onSent={onSent}
            trigger={
              <Button
                variant="outline"
                className="h-8 rounded-sm px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                data-testid="send-template-btn"
                title="Send Template"
              >
                <Send className="h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Send Template</span>
              </Button>
            }
          />
          <SummariseThreadButton wa_id={conversation.wa_id} />
          {isAdmin && (
            <Button
              variant="outline"
              className={`h-8 rounded-sm px-2 text-xs sm:h-9 sm:px-3 sm:text-sm ${locked ? "border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950" : ""}`}
              disabled={togglingLock}
              onClick={async () => {
                setTogglingLock(true);
                try {
                  if (locked) {
                    await api.delete(`/conversations/${conversation.wa_id}/lock`);
                    toast.success("Unlocked — staff can see this chat again");
                  } else {
                    await api.post(`/conversations/${conversation.wa_id}/lock`);
                    toast.success("Locked — only admin can see this chat");
                  }
                  onLockChange?.(conversation.wa_id, !locked);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Lock toggle failed");
                } finally {
                  setTogglingLock(false);
                }
              }}
              data-testid={locked ? "unlock-conversation-btn" : "lock-conversation-btn"}
              title={locked ? "Unlock — staff will see this chat" : "Lock — hide from staff, admin only"}
            >
              {locked ? <Unlock className="h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" /> : <Lock className="h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />}
              <span className="hidden sm:inline">{locked ? "Unlock" : "Lock"}</span>
            </Button>
          )}
        </div>
      </header>
      <div
        ref={scrollRef}
        className="chat-scrollbar wa-preview-bg min-h-0 flex-1 overflow-y-auto"
        data-testid="chat-scroll-viewport"
      >
        <div className="flex flex-col gap-2 p-6" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="mx-auto rounded-sm border border-border bg-background px-3 py-1 text-xs text-muted-foreground">
              No messages yet.
            </div>
          )}
          {messages.map((m, idx) => {
            const prev = messages[idx - 1];
            const prevTs = prev?.timestamp ? new Date(prev.timestamp).getTime() : null;
            const curTs = m.timestamp ? new Date(m.timestamp).getTime() : null;
            const gapMs = prevTs && curTs ? curTs - prevTs : 0;
            const showDivider = idx === 0 || gapMs > 24 * 60 * 60 * 1000;
            return (
              <div key={m.id || m.wamid || idx}>
                {showDivider && <SessionDivider message={m} wa_id={conversation.wa_id} />}
                <div className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-md px-3 py-2 shadow-sm ${m.direction === "out" ? "wa-bubble-out" : "wa-bubble-in"}`}>
                    {m.media?.url && (m.type === "image" || m.type === "sticker") && (
                      <a href={mediaUrlFor(m)} target="_blank" rel="noreferrer" className="mb-1 block">
                        <img src={mediaUrlFor(m)} alt={m.media.filename || "media"} className="max-h-64 rounded-sm object-cover" />
                      </a>
                    )}
                    {m.media?.url && m.type === "video" && (
                      <video src={mediaUrlFor(m)} controls className="mb-1 max-h-64 w-full rounded-sm" />
                    )}
                    {m.media?.url && m.type === "audio" && (
                      <audio src={mediaUrlFor(m)} controls className="mb-1 w-full" />
                    )}
                    {m.media?.url && m.type === "document" && (
                      <a
                        href={mediaUrlFor(m)}
                        target="_blank"
                        rel="noreferrer"
                        className="mb-1 flex items-center gap-2 border border-neutral-300 bg-white px-2 py-1.5 text-xs text-neutral-800 hover:bg-neutral-50"
                        data-testid={`doc-link-${m.id || m.wamid}`}
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        <span className="truncate">{m.media.filename || "Attachment"}</span>
                        <span className="ml-auto text-[10px] text-neutral-500">
                          {(m.media.mime_type || "").split("/")[1] || "file"}
                        </span>
                      </a>
                    )}
                    <div className="whitespace-pre-wrap text-sm text-neutral-900">{m.body}</div>
                    {m.status === "failed" && (
                      <div
                        className="mt-1 border border-red-400 bg-red-50 px-2 py-1 text-[11px] text-red-800"
                        data-testid={`message-failed-${m.id || m.wamid}`}
                      >
                        <div className="font-bold uppercase tracking-widest">Not delivered</div>
                        <div className="mt-0.5 whitespace-pre-wrap">
                          {m.status_error?.reason || m.status_error?.title || "Meta rejected this message."}
                          {m.status_error?.code ? ` (code ${m.status_error.code})` : ""}
                        </div>
                      </div>
                    )}
                    <div className="mt-1 flex items-center justify-end gap-1.5 text-[10px] text-neutral-500">
                      {m.sender_name ? `${m.sender_name} · ` : ""}{formatTime(m.timestamp)}
                      {m.status ? (
                        <span className={m.status === "failed" ? "font-bold text-red-600" : ""}>
                          {" · "}{m.status}
                        </span>
                      ) : ""}
                      {(m.body === "(no body — see raw)" || m.raw) && (
                        <button
                          onClick={() => viewRaw(m)}
                          className="text-neutral-500 hover:text-neutral-800"
                          title="View raw payload"
                          data-testid={`view-raw-${m.id || m.wamid}`}
                        >
                          <Code2 className="h-3 w-3" />
                        </button>
                      )}
                      {isAdmin && m.direction === "out" && m.body && m.type !== "template" && !m.from_bot && m.status !== "failed" && (
                        <button
                          onClick={() => saveAsBotAnswer(m)}
                          className="text-neutral-500 hover:text-emerald-600"
                          title="Save as bot answer — pairs this with the previous customer message and adds to the knowledge base"
                          data-testid={`save-bot-answer-${m.id || m.wamid}`}
                        >
                          <GraduationCap className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div data-testid="chat-bottom-anchor" />
        </div>
      </div>
      <footer className="flex-shrink-0 border-t border-border bg-background px-4 py-3">
        {!withinWindow && lastInbound && !isInterakt && (
          <div className="mb-2 border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="closed-window-notice">
            The 24-hour customer service window has closed. Free-text and media replies will be rejected — send a template to reopen the conversation.
          </div>
        )}
        {!lastInbound && !isInterakt && (
          <div className="mb-2 border border-border bg-secondary px-3 py-2 text-xs text-muted-foreground">
            Customer hasn&apos;t replied yet. Free-text is only allowed after the customer messages you first — use a template to start the conversation.
          </div>
        )}
        <div className="flex items-end gap-2">
          <SendMediaDialog wa_id={conversation.wa_id} disabled={!withinWindow} onSent={onSent} />
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={withinWindow ? "Type a reply… (Enter to send, Shift+Enter for new line)" : "24-hour window closed — send a template instead"}
            rows={2}
            maxLength={4096}
            disabled={!withinWindow || sending}
            className="min-h-[44px] resize-none rounded-sm"
            data-testid="chat-compose-input"
          />
          <Button
            onClick={sendText}
            disabled={!text.trim() || !withinWindow || sending}
            className="h-11 rounded-sm px-4"
            data-testid="chat-send-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>{text.length}/4096</span>
          <span>Free-text and media replies are only allowed inside the 24-hour customer service window.</span>
        </div>
      </footer>
    </div>
  );
}

function SendTemplateDialog({ trigger, defaultTo = "", onSent }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("meta");
  const [interaktMode, setInteraktMode] = useState("template"); // template | text
  const [templates, setTemplates] = useState([]);
  const [to, setTo] = useState(defaultTo);
  const [chosen, setChosen] = useState("");
  const [vars, setVars] = useState({});
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerMediaFilename, setHeaderMediaFilename] = useState("");
  const [freeText, setFreeText] = useState("");
  const [interaktTplName, setInteraktTplName] = useState("");
  const [interaktTplLang, setInteraktTplLang] = useState("en");
  const [interaktTplValues, setInteraktTplValues] = useState("");
  const [interaktTemplates, setInteraktTemplates] = useState([]);
  const [interaktLoading, setInteraktLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/templates").then((r) => setTemplates(r.data.filter((t) => t.status === "APPROVED")));
    setTo(defaultTo);
  }, [open, defaultTo]);

  useEffect(() => {
    if (!open || channel !== "interakt" || interaktTemplates.length) return;
    setInteraktLoading(true);
    api.get("/interakt/templates")
      .then((r) => setInteraktTemplates(Array.isArray(r.data?.templates) ? r.data.templates : []))
      .catch(() => setInteraktTemplates([]))
      .finally(() => setInteraktLoading(false));
  }, [open, channel, interaktTemplates.length]);

  const active = templates.find((t) => t.name === chosen);
  const bodyComponent = active?.components?.find((c) => (c.type || "").toUpperCase() === "BODY");
  const headerComponent = active?.components?.find((c) => (c.type || "").toUpperCase() === "HEADER");
  const headerFormat = (headerComponent?.format || "").toUpperCase();
  const needsHeaderMedia = headerFormat === "IMAGE" || headerFormat === "DOCUMENT" || headerFormat === "VIDEO";
  const bodyText = bodyComponent?.text || "";
  const placeholders = [...bodyText.matchAll(/{{\s*(\d+)\s*}}/g)].map((m) => m[1]);

  const submit = async () => {
    if (!/^\d{10}$/.test(to)) return toast.error("Enter valid 10-digit mobile");
    setSending(true);
    try {
      if (channel === "interakt") {
        if (interaktMode === "text") {
          if (!freeText.trim()) { setSending(false); return toast.error("Enter a message"); }
          await api.post("/messages/send-text", { to, body: freeText.trim(), channel: "interakt" });
        } else {
          if (!interaktTplName.trim()) { setSending(false); return toast.error("Enter template name approved on Interakt"); }
          const values = interaktTplValues.split(/[,|]/).map((v) => v.trim()).filter(Boolean);
          const components = values.length
            ? [{ type: "body", parameters: values.map((v) => ({ type: "text", text: v })) }]
            : undefined;
          await api.post("/messages/send-template", {
            to,
            channel: "interakt",
            template_name: interaktTplName.trim(),
            language: interaktTplLang.trim() || "en",
            components,
          });
        }
      } else {
        if (!active) { setSending(false); return toast.error("Pick a template"); }
        const components = [];
        if (needsHeaderMedia) {
          if (!/^https?:\/\//.test(headerMediaUrl.trim())) {
            setSending(false);
            return toast.error("Paste a public http(s) URL for the header file");
          }
          const mediaType = headerFormat.toLowerCase(); // image | document | video
          const mediaObj = { link: headerMediaUrl.trim() };
          if (mediaType === "document") {
            mediaObj.filename = (headerMediaFilename.trim() ||
              headerMediaUrl.trim().split("/").pop() ||
              "document.pdf");
          }
          components.push({
            type: "header",
            parameters: [{ type: mediaType, [mediaType]: mediaObj }],
          });
        }
        if (placeholders.length > 0) {
          components.push({
            type: "body",
            parameters: placeholders.map((p) => ({ type: "text", text: vars[p] || "" })),
          });
        }
        await api.post("/messages/send-template", {
          to, channel: "meta",
          template_name: active.name, language: active.language,
          components: components.length ? components : undefined,
        });
      }
      toast.success("Sent");
      const waIdSent = `91${to}`;
      setOpen(false);
      setChosen(""); setVars({}); setFreeText("");
      setHeaderMediaUrl(""); setHeaderMediaFilename("");
      setInteraktTplName(""); setInteraktTplValues("");
      onSent?.(waIdSent, channel);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Start a conversation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Send from</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {[
                { v: "meta", label: "Direct Meta", phone: "+91 77107 75858", note: "Template only to initiate · free-text inside 24h", cls: "border-primary/50 bg-primary/5", badge: "meta" },
                { v: "interakt", label: "Interakt", phone: "+91 77101 17777", note: "Coexistence · pick Template or Text below", cls: "border-fuchsia-500/50 bg-fuchsia-500/5", badge: "interakt" },
              ].map((c) => (
                <button
                  type="button"
                  key={c.v}
                  onClick={() => setChannel(c.v)}
                  data-testid={`send-channel-${c.v}`}
                  className={`flex flex-col gap-0.5 border p-3 text-left transition-colors ${
                    channel === c.v ? c.cls : "border-border hover:bg-secondary"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <ChannelBadge channel={c.badge} />
                    <span className="text-sm font-bold">{c.label}</span>
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">{c.phone}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground">{c.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Recipient (10-digit)</Label>
            <div className="mt-1 flex border border-input">
              <span className="border-r border-input bg-muted px-3 py-2 text-sm text-muted-foreground">+91</span>
              <Input
                value={to}
                maxLength={10}
                onChange={(e) => setTo(e.target.value.replace(/\D/g, ""))}
                className="rounded-none border-0 focus-visible:ring-0"
                data-testid="send-to-input"
              />
            </div>
          </div>

          {channel === "meta" ? (
            <>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em]">Approved template</Label>
                <Select value={chosen} onValueChange={setChosen}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="send-template-select">
                    <SelectValue placeholder={templates.length ? "Choose approved template" : "No approved templates"} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} <span className="ml-2 text-xs text-muted-foreground">({t.language})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {active && (
                <div className="border border-border bg-secondary p-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Body preview</div>
                  <div className="mt-1 whitespace-pre-wrap text-sm">{bodyText}</div>
                  {needsHeaderMedia && (
                    <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                      Header requires a {headerFormat.toLowerCase()} attachment
                    </div>
                  )}
                </div>
              )}
              {needsHeaderMedia && (
                <>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">
                      Header {headerFormat.toLowerCase()} · public URL
                    </Label>
                    <Input
                      value={headerMediaUrl}
                      onChange={(e) => setHeaderMediaUrl(e.target.value)}
                      placeholder={headerFormat === "DOCUMENT" ? "https://files.example.com/invoice-123.pdf" : "https://files.example.com/promo.jpg"}
                      className="mt-1 rounded-sm font-mono text-xs"
                      data-testid="send-header-media-url"
                    />
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      Must be a reachable HTTPS link. WhatsApp downloads it from Meta's servers.
                    </p>
                  </div>
                  {headerFormat === "DOCUMENT" && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">Filename shown to patient</Label>
                      <Input
                        value={headerMediaFilename}
                        onChange={(e) => setHeaderMediaFilename(e.target.value)}
                        placeholder="Invoice-123.pdf"
                        className="mt-1 rounded-sm"
                        data-testid="send-header-media-filename"
                      />
                    </div>
                  )}
                </>
              )}
              {placeholders.map((p) => (
                <div key={p}>
                  <Label className="text-xs font-bold uppercase tracking-[0.2em]">Variable {`{{${p}}}`}</Label>
                  <Input
                    className="mt-1 rounded-sm"
                    value={vars[p] || ""}
                    onChange={(e) => setVars((v) => ({ ...v, [p]: e.target.value }))}
                    data-testid={`var-input-${p}`}
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="flex gap-1 border border-border p-0.5">
                {[
                  { v: "template", label: "Template (initiate)" },
                  { v: "text", label: "Text (needs 24h window)" },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setInteraktMode(m.v)}
                    data-testid={`interakt-mode-${m.v}`}
                    className={`flex-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                      interaktMode === m.v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {interaktMode === "template" ? (
                <>
                  <div className="border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 text-xs text-muted-foreground">
                    Enter a template name exactly as approved in Interakt&apos;s dashboard. Templates can initiate a fresh conversation anytime.
                  </div>
                  <div className="grid grid-cols-[1fr_120px] gap-2">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">Template name</Label>
                      {interaktTemplates.length > 0 ? (
                        <Select value={interaktTplName} onValueChange={(v) => {
                          setInteraktTplName(v);
                          const found = interaktTemplates.find((t) => (t.name || t.template_name) === v);
                          if (found) setInteraktTplLang(found.language || found.languageCode || found.language_code || "en");
                        }}>
                          <SelectTrigger className="mt-1 rounded-sm" data-testid="interakt-template-name">
                            <SelectValue placeholder="Choose approved Interakt template" />
                          </SelectTrigger>
                          <SelectContent>
                            {interaktTemplates.map((t) => {
                              const n = t.name || t.template_name;
                              const lang = t.language || t.languageCode || t.language_code || "";
                              const status = t.status || t.template_status || "";
                              return (
                                <SelectItem key={n} value={n}>
                                  {n} <span className="ml-2 text-xs text-muted-foreground">({lang}{status ? ` · ${status}` : ""})</span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={interaktTplName}
                          onChange={(e) => setInteraktTplName(e.target.value.trim())}
                          placeholder={interaktLoading ? "Loading templates…" : "appointment_reminder"}
                          className="mt-1 rounded-sm font-mono"
                          data-testid="interakt-template-name"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-[0.2em]">Language</Label>
                      <Input
                        value={interaktTplLang}
                        onChange={(e) => setInteraktTplLang(e.target.value.trim())}
                        placeholder="en"
                        className="mt-1 rounded-sm font-mono"
                        data-testid="interakt-template-lang"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Body variables (comma or | separated)</Label>
                    <Textarea
                      rows={3}
                      value={interaktTplValues}
                      onChange={(e) => setInteraktTplValues(e.target.value)}
                      placeholder="Rajesh, 12 Feb 10:30 AM, Dr. Kaur"
                      className="mt-1 rounded-sm"
                      data-testid="interakt-body-values"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="border border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-700 dark:text-amber-300">
                    WhatsApp policy: free-text can only be sent inside the 24-hour customer service window. If Interakt rejects with &quot;customer not available&quot;, switch to Template above.
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.2em]">Message</Label>
                    <Textarea
                      value={freeText}
                      onChange={(e) => setFreeText(e.target.value)}
                      rows={4}
                      maxLength={4096}
                      placeholder="Type the message you want to send…"
                      className="mt-1 rounded-sm"
                      data-testid="interakt-freetext"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={sending} className="rounded-sm" data-testid="confirm-send-template-btn">
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SendMediaDialog({ wa_id, disabled, onSent }) {
  const [open, setOpen] = useState(false);
  const [mediaType, setMediaType] = useState("image");
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!/^https?:\/\//.test(url)) return toast.error("Paste a public http(s) URL");
    setSending(true);
    try {
      await api.post("/messages/send-media", {
        to: wa_id,
        url: url.trim(),
        media_type: mediaType,
        caption: caption.trim() || undefined,
      });
      toast.success("Sent");
      setOpen(false);
      setUrl("");
      setCaption("");
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="h-11 shrink-0 rounded-sm px-3"
          data-testid="chat-attach-btn"
          title="Send image / document / video"
        >
          <Paperclip className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display">Send media</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Type</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {[
                { v: "image", label: "Image", Icon: ImageIcon },
                { v: "document", label: "Document", Icon: Paperclip },
                { v: "video", label: "Video", Icon: ImageIcon },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setMediaType(t.v)}
                  data-testid={`media-type-${t.v}`}
                  className={`flex items-center justify-center gap-2 border py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    mediaType === t.v ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"
                  }`}
                >
                  <t.Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Public URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/appointment-card.png"
              className="mt-1 rounded-sm font-mono text-xs"
              data-testid="media-url-input"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              WhatsApp downloads the file from this URL, so it must be publicly reachable (no auth wall).
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em]">Caption (optional)</Label>
            <Textarea
              rows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={1024}
              className="mt-1 rounded-sm"
              data-testid="media-caption-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={sending} className="rounded-sm" data-testid="confirm-send-media-btn">
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummariseThreadButton({ wa_id }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/conversations/${wa_id}/summary`, { params: { refresh } });
      setSummary(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Summary failed");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setOpen(true);
    if (!summary) load(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          onClick={openDialog}
          className="h-8 rounded-sm px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
          data-testid="summarise-thread-btn"
          title="AI summary of this conversation"
        >
          <Sparkles className="h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">Summarise</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Sparkles className="h-4 w-4 text-primary" /> Thread summary
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {loading && (
            <div className="border border-border bg-secondary p-6 text-sm text-muted-foreground" data-testid="summary-loading">
              Reading the last 150 messages and generating a clinical summary…
            </div>
          )}
          {!loading && summary && (
            <>
              <div className="whitespace-pre-wrap border border-border bg-secondary p-4 text-sm leading-relaxed" data-testid="summary-body">
                {summary.summary}
              </div>
              <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                <span>Based on {summary.message_count} messages</span>
                <span>{summary.generated_at?.slice(0, 16).replace("T", " ")}</span>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Close</Button>
          <Button onClick={() => load(true)} disabled={loading} className="rounded-sm" data-testid="regenerate-summary-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

